import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { 
  Building2, 
  Calendar, 
  Clock, 
  ArrowLeft,
  Euro,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  FileText,
  Briefcase
} from 'lucide-react';

export default function NouveauxSeuilsArticle() {
  return (
    <>
      <SEO 
        title="Nouveaux Seuils Marchés Publics 2024-2025"
        description="Les nouveaux seuils des marchés publics 2024-2025 : MAPA, procédures formalisées, marchés européens. Mise à jour des montants."
        path="/blog/nouveaux-seuils-marches-publics-2024"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                Réglementation
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                NOUVEAU
              </span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Nouveaux Seuils Marchés Publics 2026 : 60k€ et 100k€ Décryptés
            </h1>
            <div className="flex items-center gap-6 text-slate-500">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                15 février 2026
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                8 min de lecture
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-12 mb-12 text-white text-center">
            <Euro className="w-20 h-20 mx-auto mb-4 opacity-90" />
            <p className="text-xl font-medium">Décrets du 29 décembre 2025</p>
            <p className="text-blue-100">Simplification historique des seuils</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Les décrets du <strong>29 décembre 2025</strong> (n°2025-1383 et n°2025-1386) marquent une vague de 
              simplification historique de la commande publique. Le seuil de dispense de publicité et de mise en 
              concurrence passe à <strong>60 000 € pour les fournitures et services</strong> (au lieu de 40 000 €), 
              et le seuil de <strong>100 000 € pour les travaux est pérennisé</strong>. Décryptage complet.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-blue-900 mb-2">Impact Fort - 9/10</h4>
                  <p className="text-blue-800">
                    Davantage de marchés pourront être traités de gré à gré, simplifiant l'accès 
                    à la commande publique pour les PME et TPE.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 text-blue-600" />
              Calendrier des nouveaux seuils
            </h2>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 my-8 overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-blue-200">
                    <th className="text-left py-3 text-blue-900">Date</th>
                    <th className="text-left py-3 text-blue-900">Mesure</th>
                    <th className="text-left py-3 text-blue-900">Impact</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-b border-blue-100">
                    <td className="py-3 font-medium">1er janvier 2026</td>
                    <td className="py-3">Seuil dispense travaux <strong>100 000 €</strong> pérennisé</td>
                    <td className="py-3"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">Fort</span></td>
                  </tr>
                  <tr className="border-b border-blue-100">
                    <td className="py-3 font-medium">1er janvier 2026</td>
                    <td className="py-3">Plafond CA exigible : <strong>1,5x</strong> montant marché (vs 2x)</td>
                    <td className="py-3"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">Fort</span></td>
                  </tr>
                  <tr className="border-b border-blue-100">
                    <td className="py-3 font-medium">1er janvier 2026</td>
                    <td className="py-3">Nouveaux seuils UE travaux : <strong>5 404 000 €</strong></td>
                    <td className="py-3"><span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm">Moyen</span></td>
                  </tr>
                  <tr className="border-b border-blue-100">
                    <td className="py-3 font-medium">1er avril 2026</td>
                    <td className="py-3">Seuil dispense fournitures/services : <strong>60 000 €</strong></td>
                    <td className="py-3"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">Fort</span></td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium">1er avril 2026</td>
                    <td className="py-3">Dématérialisation obligatoire alignée sur <strong>60 000 €</strong></td>
                    <td className="py-3"><span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm">Moyen</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <TrendingUp className="w-7 h-7 text-blue-600" />
              Ce qui change concrètement
            </h2>

            <h3 className="text-xl font-bold text-slate-800 mt-8 mb-4">
              1. Seuil de 60 000 € pour fournitures et services
            </h3>

            <p>
              Depuis le <strong>1er avril 2026</strong>, les acheteurs publics peuvent passer des marchés de fournitures 
              et services <strong>sans publicité ni mise en concurrence</strong> jusqu'à 60 000 € HT (contre 40 000 € auparavant).
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h4 className="font-bold text-red-900 mb-3">Avant (jusqu'au 31 mars 2026)</h4>
                <ul className="text-red-800 space-y-2">
                  <li>• Seuil de dispense : <strong>40 000 € HT</strong></li>
                  <li>• MAPA obligatoire dès 40 001 €</li>
                  <li>• Publication sur profil d'acheteur</li>
                </ul>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-900 mb-3">Après (à partir du 1er avril 2026)</h4>
                <ul className="text-green-800 space-y-2">
                  <li>• Seuil de dispense : <strong>60 000 € HT</strong></li>
                  <li>• MAPA obligatoire dès 60 001 €</li>
                  <li>• Plus de flexibilité pour les acheteurs</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-800 mt-8 mb-4">
              2. Seuil de 100 000 € pour les travaux pérennisé
            </h3>

            <p>
              Le seuil de 100 000 € pour les marchés de travaux, initialement temporaire (régime ASAP), est désormais 
              <strong>définitivement inscrit dans le droit</strong> depuis le 1er janvier 2026.
            </p>

            <div className="bg-slate-100 rounded-xl p-6 my-8">
              <h4 className="font-bold text-slate-900 mb-4">Rappel des seuils travaux 2026</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span><strong>0 - 100 000 € HT</strong> : Marché de gré à gré (sans publicité)</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span><strong>100 001 € - 5 382 000 € HT</strong> : MAPA avec publicité adaptée</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-violet-600" />
                  <span><strong>Au-delà de 5 382 000 € HT</strong> : Procédure formalisée (appel d'offres)</span>
                </li>
              </ul>
            </div>

            <h3 className="text-xl font-bold text-slate-800 mt-8 mb-4">
              3. Plafond de chiffre d'affaires réduit
            </h3>

            <p>
              Une excellente nouvelle pour les PME : le décret 2025-1383 limite le chiffre d'affaires exigible 
              à <strong>1,5 fois le montant du marché</strong> (contre 2 fois auparavant).
            </p>

            <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 my-8">
              <h4 className="font-bold text-violet-900 mb-3">Exemple concret</h4>
              <p className="text-violet-800">
                Pour un marché de 200 000 €, l'acheteur ne peut plus exiger un CA minimum de 400 000 € 
                mais seulement de <strong>300 000 €</strong>. Cette mesure facilite l'accès des PME aux 
                marchés de taille moyenne.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Briefcase className="w-7 h-7 text-blue-600" />
              Impact pour les entreprises
            </h2>

            <div className="space-y-4 my-8">
              <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-green-900">Plus d'opportunités en gré à gré</h4>
                  <p className="text-green-800">Les marchés entre 40 000 € et 60 000 € pourront désormais être attribués directement, sans procédure de mise en concurrence formelle.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-green-900">Accès facilité pour les PME</h4>
                  <p className="text-green-800">La réduction du plafond de CA exigible ouvre l'accès à davantage de marchés pour les entreprises de taille modeste.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-amber-900">Attention : importance du référencement</h4>
                  <p className="text-amber-800">Avec moins de publicité obligatoire, il devient crucial d'être bien référencé auprès des acheteurs pour être consulté.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Tableau récapitulatif des seuils 2026
            </h2>

            <div className="bg-slate-100 rounded-xl p-6 my-8 overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 text-slate-900">Type de marché</th>
                    <th className="text-left py-3 text-slate-900">Dispense</th>
                    <th className="text-left py-3 text-slate-900">MAPA</th>
                    <th className="text-left py-3 text-slate-900">Formalisé</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-b border-slate-200">
                    <td className="py-3 font-medium">Fournitures & Services</td>
                    <td className="py-3">0 - 60 000 €</td>
                    <td className="py-3">60 001 € - 143 000 €</td>
                    <td className="py-3">&gt; 143 000 €</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 font-medium">Travaux</td>
                    <td className="py-3">0 - 100 000 €</td>
                    <td className="py-3">100 001 € - 5 382 000 €</td>
                    <td className="py-3">&gt; 5 382 000 €</td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium">Services sociaux</td>
                    <td className="py-3">0 - 60 000 €</td>
                    <td className="py-3" colSpan="2">60 001 € - 750 000 € (procédure adaptée)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Conclusion : adaptez votre stratégie
            </h2>

            <p>
              La simplification des seuils 2026 représente une <strong>opportunité majeure</strong> pour les entreprises 
              qui savent s'y adapter. Deux stratégies complémentaires s'imposent :
            </p>

            <ol className="space-y-3 my-6">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                <span><strong>Développez votre réseau</strong> : les marchés sous les seuils sont attribués sur consultation directe, le relationnel devient déterminant.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                <span><strong>Restez visible</strong> : utilisez les outils de veille pour identifier les marchés publiés et maintenir votre présence.</span>
              </li>
            </ol>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">
              Ne manquez aucun marché grâce à notre veille IA
            </h3>
            <p className="text-violet-100 mb-6">
              Notre IA surveille 8+ sources et vous alerte sur les opportunités adaptées à votre profil.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-700 hover:bg-slate-100">
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
